import React, { useEffect, useState } from 'react';
import {
  Box, Button, Flex, Input, Select, Table, Tbody, Td, Text, Th, Thead, Tr,
} from '@chakra-ui/react';
import { useLazyExportTenantAccessAuditQuery, useSearchTenantAccessAuditQuery } from '../../services/apis/tenantAccessAPI';
import SimplePagination from '../ElementaryComponents/SimplePagination';
import StatusAlert from '../ElementaryComponents/StatusAlert';
import { NAVY, Panel, navyPress } from './TenantAccessLayout';

const NameEmail = ({ name, email }) => (
  <>
    {name}
    <Text fontSize="xs" color="gray.600">{email}</Text>
  </>
);

// The page names the file. A blob download does not use the API filename.
const auditFileName = () => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `Tenant Access Audit_${month}-${day}-${now.getFullYear()}.csv`;
};

const formatWhen = (value) => {
  if (!value) {
    return '';
  }
  return new Date(value).toLocaleString();
};

const AuditLog = ({ initialJobId = '' }) => {
  const [queryInput, setQueryInput] = useState(initialJobId);
  const [query, setQuery] = useState(initialJobId);
  const [changeType, setChangeType] = useState('All');
  const [range, setRange] = useState('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  useEffect(() => {
    const timer = setTimeout(() => {
      setQuery(queryInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [queryInput]);

  const filters = {
    query, change_type: changeType, range, page_number: page, page_size: pageSize,
  };
  const audit = useSearchTenantAccessAuditQuery(filters);
  const [exportAudit, exportState] = useLazyExportTenantAccessAuditQuery();
  const total = audit.data?.meta?.total_results || 0;
  const unfiltered = audit.data?.meta?.total_unfiltered || 0;
  const rows = audit.data?.data || [];
  const update = (setter) => (value) => {
    setter(value);
    setPage(1);
  };

  // Export covers every row matching the filters, not just the current page.
  const download = async () => {
    const blob = await exportAudit({ query, change_type: changeType, range }).unwrap();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = auditFileName();
    link.click();
    URL.revokeObjectURL(url);
  };

  let body = null;
  if (audit.isFetching) {
    body = <StatusAlert status="loading" alert_title="Loading the audit log" />;
  } else if (audit.isError) {
    body = <StatusAlert status="error" alert_title="Could not load the audit log" error={audit.error} />;
  } else if (!unfiltered) {
    body = (
      <Box textAlign="center" py={8}>
        <Text fontWeight="600">No assignment activity yet</Text>
        <Text fontSize="sm" color="gray.600">Entries appear here after an assignment runs.</Text>
      </Box>
    );
  } else if (!rows.length) {
    body = (
      <Box textAlign="center" py={8}>
        <Text fontWeight="600">No matches</Text>
        <Text fontSize="sm" color="gray.600">No entries match these filters.</Text>
      </Box>
    );
  } else {
    body = (
      <>
        <Box overflowX="auto" borderWidth="1px" borderRadius="md">
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>When</Th>
                <Th>Exchange Administrator</Th>
                <Th>User</Th>
                <Th>Tenant</Th>
                <Th>App</Th>
                <Th>Change</Th>
                <Th>Detail</Th>
                <Th>Job ID</Th>
              </Tr>
            </Thead>
            <Tbody>
              {rows.map((row) => (
                <Tr key={row.id}>
                  <Td whiteSpace="nowrap">{formatWhen(row.timestamp)}</Td>
                  <Td>
                    <NameEmail name={row.administrator?.name} email={row.administrator?.email} />
                  </Td>
                  <Td><NameEmail name={row.user?.name} email={row.user?.email} /></Td>
                  <Td>{row.tenant?.name}</Td>
                  <Td>{row.app}</Td>
                  <Td>{row.change_type}</Td>
                  <Td>{row.detail}</Td>
                  <Td fontFamily="mono" fontSize="xs">{row.job_id}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
        <SimplePagination
          totalRows={total}
          rowsPerPage={pageSize}
          currentPage={page}
          setCurrentPage={setPage}
          setRowsPerPage={(size) => {
            setPageSize(size);
            setPage(1);
          }}
        />
      </>
    );
  }

  return (
    <Panel
      title="Audit log"
      subtitle="A read-only record of every assignment change."
    >
      <Flex gap={3} wrap="wrap">
        <Input
          aria-label="Search audit log"
          placeholder="Search user, email, tenant, app, administrator, or job ID"
          value={queryInput}
          onChange={(event) => setQueryInput(event.target.value)}
          maxW="420px"
        />
        <Select aria-label="Change type" value={changeType} onChange={(event) => update(setChangeType)(event.target.value)} maxW="180px">
          <option value="All">All</option>
          <option value="Added">Added</option>
          <option value="Role updated">Role updated</option>
          <option value="Failed">Failed</option>
        </Select>
        <Select aria-label="Date range" value={range} onChange={(event) => update(setRange)(event.target.value)} maxW="180px">
          <option value="7">Last 7 days</option>
          <option value="30">Last 30 days</option>
          <option value="all">All time</option>
        </Select>
        <Button
          colorScheme="blue"
          color={NAVY}
          {...navyPress}
          onClick={download}
          isDisabled={!total || exportState.isFetching}
          isLoading={exportState.isFetching}
        >
          Export to CSV
        </Button>
      </Flex>
      {body}
    </Panel>
  );
};

export default AuditLog;
